<script setup>
import { uniqBy } from 'lodash';
import {
  computed, inject, nextTick, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { useVModel } from '@vueuse/core';
import ListaDestinarios from './ListaDestinatarios.vue';
import { confirmar } from '../../libs/cifra/dialogs';
import { filtrarPorTexto } from '../../libs/arrays';
import { isBlank } from '../../libs/strings';
import { doubleRaf } from '../../libs/animate';
import ModalSimple from '../ModalSimple.vue';

const I18n = inject('I18n');

const props = defineProps({
  destinatarios: { type: Array, required: true },
  nombreModelo: { type: String, default: null },
  solicitarInformacion: { type: Boolean, default: false },
  verLeidos: { type: Boolean, default: false },
  btnEliminar: { type: Boolean, default: true },
  btnAnadir: { type: Boolean, default: false },
  btnEliminarTodos: { type: Boolean, default: false },
  titulo: { type: String, default: null },
});
const emits = defineEmits(['update:destinatarios']);

const destinatariosInternos = useVModel(props, 'destinatarios', emits, { passive: true });
const idsDestinatariosUsuarios = computed(
  () => (destinatariosInternos.value.filter((d) => !d.mensajeria_direccion)).map((d) => d.id),
);
const idsDestinatariosMensajeria = computed(
  () => (destinatariosInternos.value.filter((d) => !!d.mensajeria_direccion)).map((d) => d.id),
);

const tituloFinal = computed(() => (
  props.titulo || I18n.t('Destinatarios')
));

const numDestinatarios = computed(() => (
  destinatariosInternos.value.length
));

const mostrarDestinatarios = computed(() => numDestinatarios.value < 4);

// *************** Acciones de destinatarios
function eliminarDestinatario(destinatario) {
  destinatariosInternos.value.splice(
    destinatariosInternos.value.findIndex((d) => d.id === destinatario.id),
    1,
  );
}

function eliminarDestinatarios() {
  if (destinatariosInternos.value.length) {
    confirmar({
      title: 'Quitar destinatarios',
      text: 'Se quitarán todos los destinatarios añadidos hasta ahora, ¿está seguro?',
    }).then((confirmado) => {
      if (confirmado) {
        destinatariosInternos.value = [];
      }
    });
  }
}

function addDestinatarios(destinatarios) {
  if (!destinatarios) {
    return;
  }
  destinatariosInternos.value = uniqBy(
    destinatariosInternos.value.concat(destinatarios),
    'id',
  );
}

// ********************** Tema del modal de destinatarios
const showModalDestinatarios = ref(false);
const inputBuscar = ref();

function mostrarModalDestinatarios() {
  showModalDestinatarios.value = true;
  nextTick(() => {
    inputBuscar.value.focus();
  });
}

// Conectamos al form:success del modal de selección de destintarios
/* global Cifra */
onMounted(() => {
  Cifra.Channels.ModalHtml.on('form:success', (view, seleccionDestinatario) => {
    // comprobamos que el componente es es de nuestra vista y que es un selector de destinatarios
    // para evitar que añada destinatarios a otras vistas del tipo contenedor
    if (view.el && view.getModal().find('#selector-destinatarios').length) {
      if (seleccionDestinatario && seleccionDestinatario.destinatarios) {
        addDestinatarios(seleccionDestinatario.destinatarios);

        view.hideModal();
      }
    }
  });
});

onUnmounted(() => {
  Cifra.Channels.ModalHtml.on('off');
});

// ********* Tema filtro y gestión de destinatarios visibles *******
const DESTINATARIOS_LIMITE = 1000;
const verTodosDestinatarios = ref(false);
const filtrandoDestinatarios = ref(false);

// Es el valor del input pero el filtro real es al dar al botón con la variable filtroDestinatarios
const valorInputBuscar = ref();
const filtroDestinatarios = ref();

const filtrando = computed(() => (!isBlank(filtroDestinatarios.value)));

// True si hay que mostrar el botón de ver todos porque hay muchos registros y todavía no
// se ha activado el botón etc.
const mostrarBotonVerTodos = computed(() => (
  !verTodosDestinatarios.value
    && !filtrando.value
    && destinatariosInternos.value.length > DESTINATARIOS_LIMITE
));

const destinatariosFiltrados = computed(() => {
  if (!showModalDestinatarios.value) {
    return [];
  }

  // No mostramos nada, se muestra el mensaje de avisando que son muchos registros
  if (mostrarBotonVerTodos.value) {
    return [];
  }

  return filtrarPorTexto(
    destinatariosInternos.value, filtroDestinatarios.value, { campo: 'nombre' },
  );
});

function filtrarDestinatarios() {
  filtrandoDestinatarios.value = true;
  doubleRaf(() => {
    filtroDestinatarios.value = valorInputBuscar.value;
    filtrandoDestinatarios.value = false;
  });
}

function limpiarFiltroDestinatarios() {
  // nextTick para que lo haga después de destruir el modal
  nextTick(() => {
    valorInputBuscar.value = '';
    verTodosDestinatarios.value = false;
    filtrandoDestinatarios.value = false;
    filtrarDestinatarios();
  });
}

function mostrarTodosDestinatarios() {
  filtrandoDestinatarios.value = true;
  doubleRaf(() => {
    verTodosDestinatarios.value = true;
    filtrandoDestinatarios.value = false;
  });
}

// Lanzamos evento javascript normal. Se usa por ejemplo en los mensajes para que se
// entere de que ha cambiado y guarde el borrador
const contenedorDestinatarios = ref();

watch(destinatariosInternos, () => {
  const event = new CustomEvent(
    'change',
    { detail: { destinatarios: destinatariosInternos.value } },
  );

  contenedorDestinatarios.value.dispatchEvent(event);
}, { deep: true });

watch(showModalDestinatarios, (nuevoValor) => {
  if (nuevoValor === false) {
    limpiarFiltroDestinatarios();
  }
});

</script>

<template>
  <div class="form-group">
    <ModalSimple
      v-model="showModalDestinatarios"
      :titulo="tituloFinal"
      tamano="lg"
    >
      <form @submit.prevent="filtrarDestinatarios">
        <div class="input-group m-b-xl">
          <InputBase ref="inputBuscar" v-model="valorInputBuscar"></InputBase>
          <span class="input-group-btn">
            <button class="btn btn-cifra">
              <i v-if="filtrandoDestinatarios" class="fa fa-circle-notch fa-spin"></i>
              <i v-else class="fa fa-search"></i>
              {{ $t('Buscar') }}
            </button>
          </span>
        </div>
      </form>
      <ListaDestinarios
        v-if="showModalDestinatarios"
        :destinatarios="destinatariosFiltrados"
        :solicitar-informacion="solicitarInformacion"
        :btn-eliminar="btnEliminar"
        :inline="true"
        class="lista-dfasdfas"
        @eliminar-destinatario="eliminarDestinatario($event)"
      />
      <div v-if="mostrarBotonVerTodos" class="text-center">
        <div class="alert alert-info">
          {{ $t('demasiados_destinatarios_alert') }}
        </div>
        <button
          class="btn btn-default"
          :disabled="filtrandoDestinatarios"
          @click="mostrarTodosDestinatarios()"
        >
          <i v-if="filtrandoDestinatarios" class="fa fa-circle-notch fa-spin"></i>
          {{ $t('Ver_todos') }}
        </button>
      </div>
    </ModalSimple>
    <div
      id="contenedor-destinatarios"
      ref="contenedorDestinatarios"
      class="contenedor-destinatarios"
      data-desplegar="false"
    >
      <div class="row">
        <div class="col-xs-12">
          <label for="contenedor-destinatarios" class="control-label">
            {{ tituloFinal }}
          </label>
        </div>
      </div>
      <div class="input-group">
        <ListaDestinarios
          v-if="mostrarDestinatarios"
          :destinatarios="destinatariosInternos"
          :solicitar-informacion="solicitarInformacion"
          :btn-eliminar="btnEliminar"
          :inline="true"
          class="form-control"
          @eliminar-destinatario="eliminarDestinatario($event)"
        />
        <div v-else class="form-control destinatarios-show">
          <div
            class="clickable label label-default"
            @click="mostrarModalDestinatarios()"
          >
            <i class="fa fa-users"></i>
            {{ numDestinatarios }} destinatarios
            <a
              v-if="btnEliminar"
              class="btn-xs"
              @click.stop.prevent="eliminarDestinatarios()"
            >
              <i class="fa fa-lg fa-times"></i>
            </a>
          </div>
        </div>
        <input
          type="hidden"
          name="destinatario_mensajeria_direccion_ids"
          :value="idsDestinatariosMensajeria"
        >
        <input
          type="hidden"
          name="destinatario_usuario_ids"
          :value="idsDestinatariosUsuarios"
        >
        <div class="input-group-btn buttons-bar">
          <a
            v-if="btnAnadir"
            v-tooltip="$t('btn_anadir_title')"
            class="btn btn-cifra btn-add-destinatarios"
            role="button"
            data-controller="modal-html"
            data-cerrar-modal="false"
            data-modal-holder-id="modal-destinatarios"
            href="/seleccion_destinatarios/new"
          >
            <i class="fa fa-plus"></i>
          </a>
          <button
            v-if="verLeidos"
            type="button"
            class="btn btn-cifra"
            :title="I18n.t('Ver_leidos')"
            data-toggle="modal"
            data-target="#seguimiento-destinatarios"
          >
            <i class="fa fa-id-card"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/../assets/stylesheets/cifra/vars/main.scss";
.contenedor-destinatarios {
  .buttons-bar {
    vertical-align: top !important;
  }
}
.destinatarios-show .label {
  font-size: 13px;
  font-weight: normal;
}
</style>
